import React, { useRef, useState } from "react";
import { BiImage, BiImageAdd } from "react-icons/bi";
import { BsTrash } from "react-icons/bs";
import styled, { css } from "styled-components";

interface Props {
    onChangeHandler: (image: File | null) => void;
    readOnly?: boolean;
    initialImgUrl?: string;
}

const ImageUpload: React.FC<Props> = ({
    onChangeHandler,
    readOnly = false,
    initialImgUrl = "",
}) => {
    const [image, setImage] = useState<File | null>(null);
    const [imgUrl, setImgUrl] = useState(initialImgUrl);
    const imgRef = useRef(null); // imgRef used to preview image without link
    const [hovering, setHovering] = useState(false);

    const handleImageData = (e: any) => {
        if (readOnly) return;

        if (image || imgUrl) {
            e.stopPropagation();

            image && setImage(null);
            imgUrl && setImgUrl("");

            setHovering(false);

            const reader = new FileReader();

            reader.onload = function (e: any) {
                (imgRef.current as any).src = null;
            };

            onChangeHandler(null);
            return;
        }

        if (e.target?.files?.length) {
            const file = e.target.files[0];
            handleFile(file);
        }
    };

    const handleFileDrop =
        (type: "dragenter" | "dragover" | "dragleave" | "drop") => (e: any) => {
            if (readOnly) return;

            setHovering(type === "dragenter" || type === "dragover");
            e.stopPropagation();
            e.preventDefault();

            if (type !== "drop") return;

            const dt = e.dataTransfer;
            const file = dt.files[0];
            handleFile(file);
        };

    const handleFile = (file: File) => {
        if (readOnly) return;

        setImage(file);
        onChangeHandler(file);

        const reader = new FileReader();

        reader.onload = (e: any) => {
            (imgRef.current as any).src = e.target.result;
        };

        reader.readAsDataURL(file);
    };

    return (
        <Container
            onClick={(e) => (image || imgUrl) && handleImageData(e)}
            onDragEnter={handleFileDrop("dragenter")}
            onDragOver={handleFileDrop("dragover")}
            onDragLeave={handleFileDrop("dragleave")}
            onDrop={handleFileDrop("drop")}
            onMouseEnter={() => (image || imgUrl) && setHovering(true)}
            onMouseLeave={() => (image || imgUrl) && setHovering(false)}
        >
            {!readOnly && (
                <>
                    <HiddenInput
                        type="file"
                        accept="image/*"
                        id="imgInput"
                        onChange={handleImageData}
                        disabled={image !== null}
                    />
                    <ImageContainer
                        ref={imgRef}
                        hidden={image === null && !imgUrl}
                        src={imgUrl}
                    />
                    {hovering && (image || imgUrl) ? (
                        <ImageRemove>
                            <div>Remove Image</div>
                            <BsTrash />
                        </ImageRemove>
                    ) : (
                        <FakeInputContainer htmlFor="imgInput">
                            {!(image || imgUrl) && (
                                <PlaceholderContainer isHovering={hovering}>
                                    <div>Add Image</div>
                                    <BiImageAdd />
                                </PlaceholderContainer>
                            )}
                        </FakeInputContainer>
                    )}{" "}
                </>
            )}
            {readOnly ? (
                <>
                    {imgUrl ? (
                        <ImageContainer src={imgUrl} readOnly={readOnly} />
                    ) : (
                        <PlaceholderContainer>
                            <div>No Image</div>
                            <BiImage />
                        </PlaceholderContainer>
                    )}
                </>
            ) : null}
        </Container>
    );
};

const FlexCenterCSS = css`
    display: flex;
    justify-content: center;
    align-items: center;
`;

const Container = styled.div`
    display: flex;
    position: relative;
    max-width: 12rem;
    height: 12rem;
    overflow: hidden;
`;

const HiddenInput = styled.input`
    display: none;
`;

const FakeInputContainer = styled.label`
    ${FlexCenterCSS}
    width: 100%;
    height: 100%;
    position: absolute;
    z-index: 1;

    &:hover {
        cursor: pointer;
    }
`;

const ImageContainer = styled.img<{ hidden?: boolean; readOnly?: boolean }>`
    position: relative;
    width: 100%;
    height: 100%;
    display: ${({ hidden }) => (hidden ? "none" : "inherit")};

    &:hover {
        opacity: ${({ readOnly }) => (readOnly ? "1" : "0.5")};
    }
`;

const ImageRemove = styled.div`
    ${FlexCenterCSS}
    position: absolute;
    flex-flow: column;
    width: 100%;
    height: 100%;
    background: rgba(255, 255, 255, 0.8);
    color: ${(props) => props.theme.colors.neutral[900]};
    cursor: pointer;
`;

const PlaceholderContainer = styled.div<{ isHovering?: boolean }>`
    ${FlexCenterCSS}
    flex-flow: column;
    border: dashed 0.125rem ${(props) => props.theme.colors.neutral[300]};

    width: 100%;
    height: 100%;
    background: ${(props) =>
        props.isHovering ? props.theme.colors.neutral[25] : "default"};
    color: ${(props) => props.theme.colors.neutral[400]};

    &:hover {
        background: ${(props) => props.theme.colors.neutral[25]};
        color: ${(props) => props.theme.colors.neutral[500]};
    }
`;

export default ImageUpload;
